'use client';

import React, { useCallback } from 'react';
import clsx from 'clsx';
import type { ButtonVariantProps } from '../types';
import ButtonComponent, { ButtonVariants } from '@pixum/button';
import { useDialogStore } from 'stores/dialog';
import '../styles.css';
import { isLinkOut, getBaseUrl, handleLinkOutTracking } from 'utils';

/**
 * Renders a Button component that opens an order-ways dialog when clicked.
 * This button is used in magazine collections and other places where users need to
 * select their preferred order method (online designer, software, app, etc.).
 *
 * Uses Zustand for state management, avoiding the need for context providers.
 *
 * @param props - ButtonVariantProps including link, variant, size, device, etc.
 * @returns A Button component that triggers the order-ways dialog
 */
export function ButtonWithOrderWaysDialog(props: ButtonVariantProps) {
  const { className, link, variant = ButtonVariants.STRONG, device, size, id, onClick } = props;

  const openDialog = useDialogStore(state => state.openDialog);

  if (!link) {
    return null;
  }

  const isExternal = link.url ? isLinkOut(link.url, getBaseUrl()) : false;
  const componentClass = clsx(className, `button-for-${device}`);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (onClick) {
      onClick(e);
    }

    if (isExternal) {
      handleLinkOutTracking(link.url as string);
    }

    openDialog({
      type: link.id || 'order-ways-product',
      url: link.url,
    });
  }, [link, onClick, isExternal]);

  return (
    <ButtonComponent
      className={componentClass}
      label={link.mouseOverText || link.title}
      title={link.title as string}
      type="button"
      variant={variant}
      size={size}
      id={id}
      onClick={handleClick}
    >
      {link.title}
    </ButtonComponent>
  );
}
